import { Component, OnInit, OnDestroy, WritableSignal, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { MatterService } from '../shared/services/matter.service';
import { Matter } from '../shared/models/matter';
import { TransferFundResponseDto } from '../shared/models/transferFundResponseDto';
import { AddTransferFundDto } from '../shared/models/transferFundRequestDto';
import { RequestsService } from '../shared/services/requests.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { Currency } from '../shared/models/currency';
import { CommonService } from '../shared/services/common.service';
import { Bill } from '../shared/models/bill';
import { BillService } from '../shared/services/billService.service';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserService } from '../shared/services/user.service';
import { Authoriser } from '../shared/models/authoriser';
import { BankAccountService } from '../shared/services/bank-account.service';
import { BankAccount } from '../shared/models/bankAccount';
import { RequestType } from '../enums/request-type.enum';
import { TransferReason } from '../enums/transfer-reason.enum';
import { buildTransferReason } from '../shared/utils/transfer-reason';
import { DraftEditor, DraftSaveOutcome } from '../shared/draft-editor/draft-editor';
import { DraftSnapshot, readBoolean, readNumber, readString, readStringMap } from '../shared/draft-editor/draft-snapshot';

@Component({
    selector: 'xp-transfer-funds',
    templateUrl: './transfer-funds.component.html',
    styleUrl: './transfer-funds.component.scss',
    providers: [DraftEditor]
})
export class TransferFundsComponent implements OnInit, OnDestroy {

    /** Saves this form as a draft and reopens saved drafts. A transfer has no documents. */
    readonly draftEditor = inject(DraftEditor);
    private readonly route = inject(ActivatedRoute);

    currentDate = new Date();
    transferFundsForm!: FormGroup;

    isUserPartner: boolean | null = null;
    isLoading = false;
    noData = false;
    submitBtn = false;
    loading = false;
    /** The Client box is read-only, unless the chosen matter has no client to fill it with: then the user can enter one. */
    clientEditable = false;

    matterNo = '';
    matterDesc = '';
    previousMatterNumber: string | null = null;

    matterDropdownData: Matter[] = [];
    currencyDropdown: Currency[] = [];
    partnerList: Authoriser[] = [];
    bankAccounts: BankAccount[] = [];
    filteredBankAccounts: BankAccount[] = [];

    bills: Bill[] = [];
    displayedColumns: string[] = ['pay', 'warning', 'amount', 'fullAmount'];
    currencySymbol: string | undefined;
    invoiceMap: { [key: string]: { totalAmount: number; balance: number } } = {};
    noBillsFoundMessage: string | null = null;

    readonly paymentMethodDropdown: string[] = ['Electronic funds transfer'];

    private readonly defaultReason = TransferReason.BillPayment;
    private reasonEditedByUser = false;

    actionstepMatterUrl = '';
    actionstepMatterOverviewUrl = '';
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);

    private readonly destroy$ = new Subject<void>();
    private actionstepBaseUrl = '';
    private actionstepOverviewBaseUrl = '';

    constructor(
        private formBuilder: FormBuilder,
        private commonService: CommonService,
        private matterService: MatterService,
        private snackbarService: SnackbarService,
        private requestService: RequestsService,
        private billService: BillService,
        private manageUserRoleService: ManageUserRoleService,
        private userService: UserService,
        private bankAccountService: BankAccountService
    ) { }

    async ngOnInit(): Promise<void> {
        this.draftEditor.configure(RequestType.TransferFund);
        this.buildForm();
        this.draftEditor.watchForm(() => ({ snapshot: this.buildDraftSnapshot(), pendingFiles: 0 }));
        await this.loadInitialData();
        // Started before restoring a draft so each invoice's remaining balance is known when its limit is set.
        const invoicesLoaded = this.getTransferFunds();
        await this.restoreDraftFromRoute(invoicesLoaded);
        this.setupMatterAutocomplete();
        this.draftEditor.markClean();
    }

    /** Whether the form holds changes that have not been saved. Used to warn the user before they leave. */
    hasUnsavedChanges(): boolean {
        return this.draftEditor.hasUnsavedChanges();
    }

    // ── Drafts ────────────────────────────────────────────────────────────────

    /** True when there is something worth saving as a draft: the user has typed something, or this is a saved draft. */
    get canSaveDraft(): boolean {
        return this.transferFundsForm.dirty || this.draftEditor.draftId() !== null;
    }

    /**
     * Saves the form as a draft, however incomplete it is.
     * @returns True when the draft was saved.
     */
    async saveDraft(): Promise<boolean> {
        const f = this.transferFundsForm.getRawValue();
        const amount = parseFloat(f.amount);

        const outcome = await this.draftEditor.save({
            snapshot: this.buildDraftSnapshot(),
            summary: {
                matterNo: Number(this.matterNo) || null,
                matter: this.matterDesc || null,
                clientName: f.client || null,
                amount: amount > 0 ? amount : null,
                reason: f.reason || null
            },
            newFiles: []
        });
        return outcome === DraftSaveOutcome.Saved;
    }

    /**
     * Handles the Save as Draft button: saves the draft and, once it is saved, clears the form for the next request,
     * as after a submit. If it could not be saved, the form stays as it is.
     * @param formDirective The form's directive, so the form is reset together with its submitted state.
     */
    async saveDraftAndReset(formDirective: FormGroupDirective): Promise<void> {
        if (!await this.saveDraft()) return;

        this.resetForm(formDirective);
        await this.draftEditor.afterDraftSaved();
    }

    private async restoreDraftFromRoute(invoicesLoaded: Promise<void>): Promise<void> {
        const draftId = Number(this.route.snapshot.paramMap.get('draftId'));
        if (!Number.isInteger(draftId) || draftId <= 0) return;

        const snapshot = await this.draftEditor.open(draftId);
        if (snapshot) {
            await invoicesLoaded.catch(() => undefined);
            await this.applyDraftSnapshot(snapshot);
        }
    }

    private buildDraftSnapshot(): DraftSnapshot {
        const f = this.transferFundsForm.getRawValue();
        const bankAccount = f.clientBankAccount as BankAccount | '' | null;

        // Invoice amounts are kept by bill id, because the invoice controls are named after the bill.
        const invoices: Record<string, string> = {};
        this.bills.forEach(bill => {
            const value = this.transferFundsForm.get(this.getControlName(bill))?.value;
            if (parseFloat(value) > 0) {
                invoices[String(bill.id)] = String(value);
            }
        });

        return {
            values: {
                matter: typeof f.matter === 'string' ? f.matter : '',
                client: f.client ?? '',
                reason: f.reason ?? '',
                paymentMethod: f.paymentMethod ?? '',
                clientBankAccountId: bankAccount ? bankAccount.accountId : '',
                partnerAuthorisingId: f.partnerAuthorisingId ?? null,
                fundsCheckedByPartner: f.fundsCheckedByPartner === true,
                invoices
            },
            state: { matterNo: this.matterNo, matterDesc: this.matterDesc }
        };
    }

    private async applyDraftSnapshot(snapshot: DraftSnapshot): Promise<void> {
        const { values, state } = snapshot;

        this.matterNo = readString(state, 'matterNo');
        this.matterDesc = readString(state, 'matterDesc');
        this.previousMatterNumber = this.matterNo || null;
        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        const bankAccountId = readString(values, 'clientBankAccountId');
        const partnerId = readNumber(values, 'partnerAuthorisingId');
        this.transferFundsForm.patchValue({
            matter: readString(values, 'matter'),
            client: readString(values, 'client'),
            reason: readString(values, 'reason'),
            paymentMethod: readString(values, 'paymentMethod') || this.paymentMethodDropdown[0],
            clientBankAccount: this.bankAccounts.find(account => account.accountId === bankAccountId) ?? '',
            // A saved partner who has since lost that role is no longer in the list; the user must choose again.
            partnerAuthorisingId: this.partnerList.some(partner => partner.id === partnerId) ? partnerId : null,
            fundsCheckedByPartner: readBoolean(values, 'fundsCheckedByPartner')
        });

        const savedReason = readString(values, 'reason');

        if (!this.matterNo) {
            this.restoreReasonMode(savedReason);
            return;
        }

        this.reasonEditedByUser = true;

        // Not waited for: it only decides whether the Client box can be edited.
        this.updateClientEditable(Number(this.matterNo));

        // The invoice lines only exist once the matter's invoices have loaded, so fill them in as that finishes.
        const saved = readStringMap(values, 'invoices');
        await new Promise<void>(resolve => this.onMatterChange(() => {
            let restored = 0;
            this.bills.forEach(bill => {
                const amount = saved[String(bill.id)];
                if (amount !== undefined) {
                    this.transferFundsForm.get(this.getControlName(bill))?.setValue(amount);
                    restored++;
                }
            });
            if (restored < Object.keys(saved).length) {
                this.snackbarService.errorSnackBar('Some invoices saved in this draft are no longer available and were removed.');
            }
            this.restoreReasonMode(savedReason);
            resolve();
        }));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private buildForm(): void {
        this.transferFundsForm = this.formBuilder.group({
            matter: ['', Validators.required],
            client: ['', Validators.required],
            reason: [this.defaultReason, Validators.required],

            partnerAuthorisingId: [null],

            amount: [{ value: '', disabled: true }],
            paymentMethod: ['', Validators.required],
            clientBankAccount: ['', Validators.required],

            dateRequested: [new Date(), Validators.required],
            requestedTime: [this.toTimeString(new Date()), Validators.required],
            fundsCheckedByPartner: [false],
        });
    }

    private async loadInitialData(): Promise<void> {
        try {
            await Promise.all([this.loadUserRole(), this.loadDropdownData()]);
        } catch {
            this.snackbarService.errorSnackBar('Failed to load initial data. Please refresh the page.');
        }
    }

    private async loadUserRole(): Promise<void> {
        const role = await this.manageUserRoleService.getUserPermissionsByRole(this.userService.userDetails?.userRole!);
        this.isUserPartner = role.partnerApprove;

        this.updatePartnerAuthorisingValidator();
    }

    private async loadDropdownData(): Promise<void> {
        const [bankAccounts, partners, currencies, actionstepUrl, actionstepOverviewUrl, matters] = await Promise.all([
            this.bankAccountService.getAllBankAccounts(),
            this.requestService.getPartnersList(),
            this.requestService.getAllCurrencies(),
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl(),
            this.matterService.getMatterList('', ''),
        ]);

        this.bankAccounts = bankAccounts;
        this.filteredBankAccounts = [...bankAccounts];
        this.partnerList = partners;
        this.currencyDropdown = currencies;
        this.actionstepBaseUrl = actionstepUrl;
        this.actionstepOverviewBaseUrl = actionstepOverviewUrl;
        this.matterDropdownData = matters;

        this.transferFundsForm.get('paymentMethod')?.setValue(this.paymentMethodDropdown[0]);
    }

    private setupMatterAutocomplete(): void {
        this.transferFundsForm.get('matter')!.valueChanges.pipe(
            debounceTime(300),
            switchMap(query => {
                const isNumeric = !isNaN(Number(query));
                this.isLoading = true;
                this.noData = true;
                return this.matterService.getMatterList(
                    isNumeric ? (query ?? '') : '',
                    isNumeric ? '' : (query ?? '')
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: data => {
                this.matterDropdownData = data ?? [];
                this.noData = !data?.length;
                this.isLoading = false;
            },
            error: err => {
                this.noData = true;
                this.isLoading = false;
                if (err.status === 401) this.commonService.apiSecurityError();
            }
        });
    }

    async lookupSelectionChanged(event: MatOptionSelectionChange<string>): Promise<void> {
        if (!event.isUserInput) return;
        const raw = event.source.viewValue;
        if (!raw?.includes(' - ')) return;

        [this.matterNo, this.matterDesc] = raw.split(' - ');

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.loading = true;
        let names: string[] = [];
        try {
            names = await this.matterService.getMatterClientNames(Number(this.matterNo));
        } catch {
            // The clients could not be loaded: treated like a matter with no client, so the user can enter one.
        } finally {
            this.loading = false;
        }

        const displayClient = names.join(', ');
        this.clientEditable = names.length === 0;

        this.transferFundsForm.patchValue({ client: displayClient });
        this.onMatterChange();

        if (this.previousMatterNumber && this.previousMatterNumber !== this.matterNo) {
            this.clearInvoiceControls();
            this.reasonEditedByUser = false;
            this.transferFundsForm.patchValue({
                matter: `${this.matterNo} - ${this.matterDesc}`,
                client: displayClient,
                reason: this.defaultReason,
                dateRequested: new Date(),
                paymentMethod: this.paymentMethodDropdown[0]
            });
        }

        this.previousMatterNumber = this.matterNo;
        this.updateTotalAmount();
    }

    /** Sets whether the Client box can be edited for a matter, without changing the client it holds (used for drafts). */
    private async updateClientEditable(matterNumber: number): Promise<void> {
        try {
            this.clientEditable = (await this.matterService.getMatterClientNames(matterNumber)).length === 0;
        } catch {
            this.clientEditable = true;
        }
    }

    /**
     * Loads the chosen matter's invoices and builds one amount control per invoice.
     * @param done Called once loading has finished, whether or not any invoices were found.
     */
    onMatterChange(done?: () => void): void {
        if (!this.matterNo) {
            done?.();
            return;
        }

        this.clearInvoiceControls();

        this.billService.getBillsByMatter(this.matterNo).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: data => {
                this.bills = data;
                this.noBillsFoundMessage = null;

                this.bills.forEach(bill => {
                    let maxBalance = parseFloat(bill.totalDue);
                    const invoiceEntry = this.invoiceMap[bill.ourReference];
                    if (invoiceEntry?.balance != null && invoiceEntry?.totalAmount != null) {
                        maxBalance = invoiceEntry.balance - invoiceEntry.totalAmount;
                    }

                    this.currencySymbol = new Intl.NumberFormat('en', { style: 'currency', currency: bill.currency })
                        .formatToParts(0)
                        .find(part => part.type === 'currency')?.value || bill.currency;

                    const control = this.formBuilder.control(0, [Validators.max(maxBalance)]);
                    this.transferFundsForm.setControl(`invoice${bill.id}`, control);
                    control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
                        this.updateTotalAmount();
                    });
                });

                this.updateTotalAmount();
                done?.();
            },
            error: err => {
                this.bills = [];
                this.noBillsFoundMessage = err.status === 404
                    ? 'No invoices available for the selected matter.'
                    : 'Failed to load invoices. Please try again later.';
                if (err.status !== 404) console.error('Error loading bills:', err);
                done?.();
            }
        });
    }

    updateTotalAmount(): void {
        let total = 0;
        this.bills.forEach(bill => {
            const control = this.transferFundsForm.get(`invoice${bill.id}`);
            if (control) total += parseFloat(control.value) || 0;
        });
        this.transferFundsForm.get('amount')?.setValue(Number(total).toFixed(2), { emitEvent: false });
        this.updateAutoReason();
        this.loading = false;
    }

    onReasonInput(): void {
        this.reasonEditedByUser = true;
    }

    private updateAutoReason(): void {
        if (this.reasonEditedByUser) return;

        this.setReason(this.buildAutoReason());
    }

    private buildAutoReason(): string {
        return buildTransferReason(this.bills
            .filter(bill => parseFloat(this.transferFundsForm.get(this.getControlName(bill))?.value) > 0)
            .map(bill => bill.ourReference));
    }

    private setReason(reason: string): void {
        this.transferFundsForm.get('reason')?.setValue(reason, { emitEvent: false });
    }

    private restoreReasonMode(savedReason: string): void {
        const generated = this.buildAutoReason();
        this.reasonEditedByUser = savedReason !== '' && savedReason !== generated;
        if (!this.reasonEditedByUser) this.setReason(generated);
    }

    getEligibleAmount(bill: Bill): number {
        const used = this.invoiceMap[bill.ourReference]?.totalAmount ?? 0;
        const eligible = (parseFloat(bill.totalDue) || 0) - used;
        return Math.max(0, Math.round(eligible * 100) / 100);
    }

    isEligibleAmountApplied(bill: Bill): boolean {
        const eligible = this.getEligibleAmount(bill);
        const amount = parseFloat(this.transferFundsForm.get(this.getControlName(bill))?.value);
        return eligible > 0 && Math.abs(amount - eligible) < 0.005;
    }

    onEligibleAmountToggle(bill: Bill, checked: boolean): void {
        const control = this.transferFundsForm.get(this.getControlName(bill));
        if (!control) return;

        control.setValue(checked ? this.getEligibleAmount(bill) : 0);
        control.markAsDirty();
    }

    formatInvoiceAmount(bill: Bill): void {
        const control = this.transferFundsForm.get(`invoice${bill.id}`);
        if (control) {
            const value = parseFloat(control.value);
            control.setValue((isNaN(value) ? 0 : value).toFixed(2), { emitEvent: true });
        }
    }


    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    private updatePartnerAuthorisingValidator(): void {
        const ctrl = this.transferFundsForm.get('partnerAuthorisingId');
        const fundsCtrl = this.transferFundsForm.get('fundsCheckedByPartner');

        if (this.isUserPartner) {
            ctrl?.clearValidators();
            fundsCtrl?.setValidators(Validators.requiredTrue);
        } else {
            ctrl?.setValidators([Validators.required]);
            fundsCtrl?.clearValidators();
        }
        ctrl?.updateValueAndValidity({ emitEvent: false });
        fundsCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    validateInput(event: KeyboardEvent): void {
        const allowedKeys = /[0-9.,]/;
        const key = event.key;
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(key)) return;
        if (!allowedKeys.test(key)) event.preventDefault();
    }

    clearSearchFilter(): void {
        this.showLinkToOpenMatter.set(false);
        this.bills = [];
        this.matterDropdownData = [];
        this.actionstepMatterUrl = '';
        this.actionstepMatterOverviewUrl = '';
        this.transferFundsForm.get('matter')?.reset();
        this.transferFundsForm.get('client')?.reset();
        this.clientEditable = false;
    }

    displayMatter(matter: Matter): string {
        return matter ? `${matter.number} - ${matter.description}` : '';
    }

    getControlName(bill: Bill): string {
        return `invoice${bill.id}`;
    }

    getInvoiceData(ourReference: string | number): { totalAmount: number; balance: number } | null {
        return this.invoiceMap[ourReference] || null;
    }

    onUrlCopied(): void {
        this.snackbarService.successSnackBar('URL copied to clipboard');
    }

    filterBankAccounts(): void {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    private clearInvoiceControls(): void {
        Object.keys(this.transferFundsForm.controls)
            .filter(key => key.startsWith('invoice'))
            .forEach(key => this.transferFundsForm.removeControl(key));
    }

    async submit(formDirective: FormGroupDirective): Promise<void> {
        // The Enter key can submit even when the button is disabled; a draft that is still being saved must not be submitted.
        if (this.draftEditor.saving()) return;

        if (this.transferFundsForm.invalid) {
            this.transferFundsForm.markAllAsTouched();
            this.snackbarService.errorSnackBar('Please complete all required fields.');
            return;
        }

        this.submitBtn = true;
        let requestCreated = false;

        try {
            const f = this.transferFundsForm.value;

            const invoices: { [key: string]: string } = {};
            this.bills.forEach(bill => {
                const amountControl = this.transferFundsForm.get(`invoice${bill.id}`);
                if (amountControl && amountControl.value > 0) {
                    invoices[`Invoice #${bill.ourReference} (Balance Due: ${this.currencySymbol}${bill.totalDue})`] = amountControl.value;
                }
            });

            const billedCurrencyCode = this.bills[0]?.currency;
            const resolvedCurrency = this.currencyDropdown.find(c => c.currencyCode === billedCurrencyCode);
            const currencyId = resolvedCurrency?.id ?? 0;

            const datePart: Date = f.dateRequested instanceof Date ? f.dateRequested : new Date(f.dateRequested);

            const [hours, minutes] = (f.requestedTime ?? '00:00').split(':').map(Number);

            const requestedDate = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, 0, 0);

            const postData: AddTransferFundDto = {
                draftId: this.draftEditor.draftId() ?? undefined,
                type: 'CO',
                matterNo: Number(this.matterNo),
                matter: this.transferFundsForm.get('matter')?.value,
                client: f.client,
                clientBankAccountName: f.clientBankAccount
                    ? `${(f.clientBankAccount as BankAccount).accountId} - ${(f.clientBankAccount as BankAccount).description}`
                    : undefined,
                clientBankAccountId: f.clientBankAccount ? (f.clientBankAccount as BankAccount).accountId : undefined,
                currencyId,
                partnerAuthorisingId: f.partnerAuthorisingId,
                amount: parseFloat(this.transferFundsForm.get('amount')?.value) || 0,
                reason: f.reason,
                invoice: JSON.stringify(invoices),
                paymentMethod: f.paymentMethod,
                dateRequested: requestedDate,
                fundsCheckedByPartner: f.fundsCheckedByPartner ?? false,
            };

            await this.requestService.addTransferFundRequest(postData);
            requestCreated = true;
            this.snackbarService.successSnackBar('Transfer request sent successfully!');
            this.resetForm(formDirective);
            this.getTransferFunds();
        } catch {
            this.snackbarService.errorSnackBar('Failed to send transfer request. Please try again.');
        } finally {
            this.submitBtn = false;
            if (requestCreated) {
                // The draft (if any) was consumed when the request was created.
                await this.draftEditor.afterSubmit();
            }
        }
    }

    resetForm(formDirective?: FormGroupDirective): void {
        this.showLinkToOpenMatter.set(false);
        this.actionstepMatterUrl = '';
        this.actionstepMatterOverviewUrl = '';
        this.bills = [];
        this.matterNo = '';
        this.matterDesc = '';
        this.clientEditable = false;
        this.previousMatterNumber = null;
        this.noBillsFoundMessage = null;
        this.clearInvoiceControls();

        this.reasonEditedByUser = false;

        const defaults = {
            dateRequested: new Date(),
            requestedTime: this.toTimeString(new Date()),
            paymentMethod: this.paymentMethodDropdown[0],
            reason: this.defaultReason
        };

        if (formDirective) {
            formDirective.resetForm(defaults);
        } else {
            this.transferFundsForm.reset(defaults);
        }

        this.transferFundsForm.get('amount')?.disable();
        this.updatePartnerAuthorisingValidator();

        setTimeout(() => {
            this.transferFundsForm.markAsPristine();
            this.transferFundsForm.markAsUntouched();
        }, 0);
    }

    async getTransferFunds(): Promise<void> {
        const response: TransferFundResponseDto[] =
            await this.requestService.getTransferFundsList(true);

        this.invoiceMap = {};

        response.forEach(item => {
            const invoiceObj = JSON.parse(item.invoice!);

            Object.keys(invoiceObj).forEach(invoiceKey => {
                const balanceText = invoiceKey.match(/Balance Due: .*?([\d.,]+)/);
                const balance = balanceText ? parseFloat(balanceText[1]) : 0;

                const invoiceNumberMatch = invoiceKey.match(/Invoice #(\d+)/);
                const invoiceNumber = invoiceNumberMatch ? invoiceNumberMatch[1] : invoiceKey;
                const invoiceAmount = parseFloat(invoiceObj[invoiceKey]) || 0;

                if (!this.invoiceMap[invoiceNumber]) {
                    this.invoiceMap[invoiceNumber] = { totalAmount: 0, balance };
                }
                this.invoiceMap[invoiceNumber].totalAmount += invoiceAmount;
            });
        });
    }
}
